/* Nomsa surrounded by noise — with Altara actions called out ON the specific screens
 * that illustrate each one. Three bold action badges appear on top of the exact screens
 * they refer to, animate in with a bouncing pop + glow, and pulse subtly.
 *
 *   1. ANALYSE MESSAGES        → WhatsApp "Are you ready to grow your wealth?" tile
 *   2. CHECK SUSPICIOUS LINKS  → "Your account will be suspended · bit.ly/3Xf9Kq" tile
 *   3. DETECT MANIPULATED MEDIA → AI cyberpunk face + audio-waveform tile (top right)
 */
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { MessageSquare, Link2, Eye } from "lucide-react";

const BG = "/prologue/nomsa_actions_bg.png";
const GOLD = "#F1C33B";

// Positions expressed as % of a 1600x900 canvas.
// Each action targets a specific screen; the frame highlights the screen edges,
// the badge sits ON the screen with big bold text.
const ACTIONS = [
  {
    id: "analyse",
    label: "ANALYSE MESSAGES",
    Icon: MessageSquare,
    frame: { l: 18.3, t: 12.5, r: 32.0, b: 35.5 },
    badge: { x: 25.5, y: 24.0 },
    delay: 0.4,
  },
  {
    id: "manipulated",
    label: "DETECT MANIPULATED MEDIA",
    Icon: Eye,
    frame: { l: 86.0, t: 3.0, r: 100.0, b: 32.0 },
    badge: { x: 76.5, y: 18.0 },
    delay: 1.3,
  },
  {
    id: "links",
    label: "CHECK SUSPICIOUS LINKS",
    Icon: Link2,
    frame: { l: 69.5, t: 26.0, r: 84.5, b: 44.5 },
    badge: { x: 70.0, y: 35.0 },
    delay: 2.2,
  },
];

export default function NomsaActionsDemo() {
  const [params] = useSearchParams();
  const embed = params.get("embed") === "1";
  // Export modes for PowerPoint click-triggered assets:
  //   asset=base   → background + gold frames only, no badges
  //   asset=badge  → only the badge whose id === which, on transparent bg
  const asset = params.get("asset");                    // "base" | "badge" | null
  const which = params.get("which");                    // action id
  const isExport = asset === "base" || asset === "badge";
  const noBadges = asset === "base";
  const noFrames = asset === "badge";
  const noBackground = asset === "badge";
  // For export we want animation to be at its final resting state immediately.
  const instant = isExport;

  const scene = (
    <div
      className="relative w-full h-full overflow-hidden"
      data-testid="nomsa-actions"
      style={{ background: noBackground ? "transparent" : "#050914" }}
    >
      {!noBackground && (
        <img
          src={BG}
          alt=""
          className="absolute inset-0 w-full h-full"
          style={{ objectFit: "cover" }}
        />
      )}
      {!noBackground && (
        <div className="absolute inset-0 pointer-events-none"
          style={{ background: "radial-gradient(ellipse at 50% 55%, transparent 40%, rgba(5,9,20,0.30) 78%, rgba(5,9,20,0.75) 100%)" }} />
      )}

      {ACTIONS
        .filter((a) => {
          if (noBadges && noFrames) return false;
          if (asset === "badge") return a.id === which;
          return true;
        })
        .map((a, i) => (
          <ActionCallout key={a.id} action={a} idx={i} instant={instant} showFrame={!noFrames} showBadge={!noBadges} />
        ))}
    </div>
  );

  if (embed || isExport) {
    return (
      <div className="w-screen h-screen" style={{ background: noBackground ? "transparent" : "#050914" }}>
        {scene}
      </div>
    );
  }
  return (
    <div className="w-full" style={{ background: "#050914", minHeight: "100vh" }}>
      <div className="mx-auto" style={{ maxWidth: 1600, aspectRatio: "16/9" }}>{scene}</div>
    </div>
  );
}

function ActionCallout({ action, idx, instant = false, showFrame = true, showBadge = true }) {
  const { frame, badge, label, Icon, delay } = action;
  // In export mode, snap straight to final state.
  const frameInitial = instant ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 1.15 };
  const frameAnimate = instant ? { opacity: 1, scale: 1 } : { opacity: [0, 1, 1], scale: [1.15, 1.02, 1.0] };
  const frameTrans = instant ? { duration: 0 } : {
    opacity: { duration: 0.5, delay, times: [0, 0.5, 1] },
    scale:   { duration: 0.7, delay, ease: "easeOut" },
  };
  const badgeInitial = instant ? { opacity: 1, scale: 1, y: 0 } : { opacity: 0, scale: 0.5, y: 20 };
  const badgeAnimate = instant ? { opacity: 1, scale: 1, y: 0 } : { opacity: 1, scale: 1, y: [0, -4, 0, 4, 0] };
  const badgeTrans = instant ? { duration: 0 } : {
    opacity: { duration: 0.5, delay: delay + 0.1 },
    scale:   { duration: 0.6, delay: delay + 0.1, type: "spring", stiffness: 220, damping: 15 },
    y: { duration: 4 + (idx % 2), delay: delay + 0.9, repeat: Infinity, ease: "easeInOut" },
  };
  return (
    <>
      {showFrame && (
        <motion.div
          className="absolute pointer-events-none"
          style={{
            left: `${frame.l}%`,
            top: `${frame.t}%`,
            width: `${frame.r - frame.l}%`,
            height: `${frame.b - frame.t}%`,
            borderRadius: 18,
            border: `3px solid ${GOLD}`,
            boxShadow: `0 0 30px ${GOLD}, 0 0 70px rgba(241,195,59,0.35), inset 0 0 24px rgba(241,195,59,0.20)`,
          }}
          initial={frameInitial}
          animate={frameAnimate}
          transition={frameTrans}
        />
      )}

      {showBadge && (
        <motion.div
          className="absolute"
          style={{
            left: `${badge.x}%`,
            top: `${badge.y}%`,
            transform: "translate(-50%, -50%)",
          }}
          initial={badgeInitial}
          animate={badgeAnimate}
          transition={badgeTrans}
        >
          {!instant && (
            <motion.div
              className="absolute inset-0 rounded-2xl"
              style={{ background: "transparent", border: `2px solid ${GOLD}` }}
              animate={{ opacity: [0.6, 0, 0.6], scale: [1, 1.15, 1] }}
              transition={{ duration: 2.4, delay: delay + 0.6, repeat: Infinity, ease: "easeInOut" }}
            />
          )}
        <div
          className="relative flex items-center gap-3 px-6 py-4 rounded-2xl"
          style={{
            background: "linear-gradient(180deg, rgba(15,20,32,0.94), rgba(8,12,20,0.94))",
            border: `2px solid ${GOLD}`,
            boxShadow: `0 12px 50px rgba(0,0,0,0.7), 0 0 40px rgba(241,195,59,0.45), inset 0 1px 0 rgba(255,255,255,0.10)`,
            backdropFilter: "blur(6px)",
            WebkitBackdropFilter: "blur(6px)",
            maxWidth: 340,
          }}
        >
          <div
            className="flex items-center justify-center rounded-xl flex-shrink-0"
            style={{ width: 40, height: 40, background: GOLD, color: "#0A1F44" }}
          >
            <Icon size={24} strokeWidth={2.6} />
          </div>
          <div
            className="font-extrabold tracking-wide uppercase"
            style={{
              color: GOLD,
              fontSize: 19,
              lineHeight: 1.05,
              letterSpacing: 0.4,
              textShadow: "0 2px 12px rgba(0,0,0,0.9), 0 0 24px rgba(241,195,59,0.35)",
            }}
          >
            {label}
          </div>
        </div>
      </motion.div>
      )}
    </>
  );
}
